/**
 * Edge Highlight Service
 *
 * Manages edge highlighting based on canvas element hover and selection.
 * When a canvas element is hovered or selected, all connected edges
 * are highlighted with 100% opacity.
 *
 * Features:
 * - Hover detection via pointermove events
 * - Selection tracking via gfx.selection
 * - Smooth transitions via CSS
 * - Multi-selection support
 */

import type { GfxController, GfxModel } from '@blocksuite/std/gfx';
import {
  FILE_INDEX_HIGHLIGHT_EVENTS,
  FILE_INDEX_SOURCE_HIGHLIGHT_CLASS,
  type FileIndexRowSourceHighlightChangeDetail,
  type FileIndexSourceElementHighlightChangeDetail,
} from '@streamlain/database-identity';

import { isFileIndexEdgeMetadata } from '../core/types';
import type { EdgeRenderingService } from '../rendering/edge-rendering-service';

/**
 * Configuration for the highlight service
 */
export interface EdgeHighlightConfig {
  /** Enable hover highlighting */
  enableHover: boolean;
  /** Enable selection highlighting */
  enableSelection: boolean;
  /** Debounce delay for hover events (ms) */
  hoverDebounceMs: number;
}

const DEFAULT_CONFIG: EdgeHighlightConfig = {
  enableHover: true,
  enableSelection: true,
  hoverDebounceMs: 16, // ~60fps
};

const SOURCE_HIGHLIGHT_STYLE_ID =
  'streamlain-file-index-source-highlight-style';

function ensureSourceHighlightStyles(documentRef: Document): void {
  if (documentRef.getElementById(SOURCE_HIGHLIGHT_STYLE_ID)) return;

  const style = documentRef.createElement('style');
  style.id = SOURCE_HIGHLIGHT_STYLE_ID;
  style.textContent = `
    .${FILE_INDEX_SOURCE_HIGHLIGHT_CLASS} {
      outline: 2px solid var(--affine-primary-color, #1e96eb) !important;
      outline-offset: 2px;
      box-shadow: 0 0 0 4px rgba(30, 150, 235, 0.16) !important;
    }
  `;
  documentRef.head.append(style);
}

/**
 * Edge Highlight Service
 *
 * Observes canvas element hover/selection and updates edge highlighting.
 */
export class EdgeHighlightService {
  private config: EdgeHighlightConfig;
  private mounted = false;
  private container: HTMLElement | null = null;

  // State tracking
  private hoveredElementId: string | null = null;
  private selectedElementIds: Set<string> = new Set();
  private readonly manualElementHighlightScopes: Map<string, Set<string>> =
    new Map();
  private highlightedEdgeIds: Set<string> = new Set();
  private highlightedElementIds: Set<string> = new Set();
  private publishedSourceBlockIds: Set<string> = new Set();

  // Event cleanup
  private cleanupFns: Array<() => void> = [];
  private hoverDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Selection polling (fallback)
  private selectionPollInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly renderingService: EdgeRenderingService,
    private readonly gfx: GfxController,
    config: Partial<EdgeHighlightConfig> = {}
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================

  /**
   * Mounts the highlight service to observe a container
   */
  mount(container: HTMLElement): void {
    if (this.mounted) {
      this.unmount();
    }

    this.container = container;
    this.mounted = true;
    ensureSourceHighlightStyles(container.ownerDocument);

    // Setup hover detection
    if (this.config.enableHover) {
      this.setupHoverDetection();
    }

    // Setup selection tracking
    if (this.config.enableSelection) {
      this.setupSelectionTracking();
    }

    this.setupFileIndexRowHighlightBridge();
  }

  /**
   * Unmounts the service and cleans up
   */
  unmount(): void {
    // Clear timers
    if (this.hoverDebounceTimer) {
      clearTimeout(this.hoverDebounceTimer);
      this.hoverDebounceTimer = null;
    }

    if (this.selectionPollInterval) {
      clearInterval(this.selectionPollInterval);
      this.selectionPollInterval = null;
    }

    // Run cleanup functions
    for (const cleanup of this.cleanupFns) {
      cleanup();
    }
    this.cleanupFns = [];

    // Clear state
    this.clearAllHighlights();
    this.hoveredElementId = null;
    this.selectedElementIds.clear();
    this.manualElementHighlightScopes.clear();
    this.publishActiveElementsChanged(new Set());

    this.container = null;
    this.mounted = false;
  }

  private setupFileIndexRowHighlightBridge(): void {
    if (!this.container) return;

    const handleRowSourceHighlightChange = (event: Event) => {
      const detail = (
        event as CustomEvent<FileIndexRowSourceHighlightChangeDetail>
      ).detail;
      if (!detail?.scope) return;
      this.setManualElementHighlights(
        detail.scope,
        detail.sourceBlockIds ?? []
      );
    };

    this.container.addEventListener(
      FILE_INDEX_HIGHLIGHT_EVENTS.rowSourceHighlightChange,
      handleRowSourceHighlightChange
    );
    this.cleanupFns.push(() => {
      this.container?.removeEventListener(
        FILE_INDEX_HIGHLIGHT_EVENTS.rowSourceHighlightChange,
        handleRowSourceHighlightChange
      );
    });
  }

  /**
   * Checks if the service is mounted
   */
  isMounted(): boolean {
    return this.mounted;
  }

  // ===========================================================================
  // Hover Detection
  // ===========================================================================

  /**
   * Sets up pointermove event handling for hover detection
   */
  private setupHoverDetection(): void {
    if (!this.container) return;

    const handlePointerMove = (e: PointerEvent) => {
      // Debounce for performance
      if (this.hoverDebounceTimer) {
        clearTimeout(this.hoverDebounceTimer);
      }

      this.hoverDebounceTimer = setTimeout(() => {
        this.processHover(e.clientX, e.clientY);
      }, this.config.hoverDebounceMs);
    };

    const handlePointerLeave = () => {
      if (this.hoverDebounceTimer) {
        clearTimeout(this.hoverDebounceTimer);
        this.hoverDebounceTimer = null;
      }
      this.setHoveredElement(null);
    };

    this.container.addEventListener('pointermove', handlePointerMove);
    this.container.addEventListener('pointerleave', handlePointerLeave);

    this.cleanupFns.push(() => {
      this.container?.removeEventListener('pointermove', handlePointerMove);
      this.container?.removeEventListener('pointerleave', handlePointerLeave);
    });
  }

  /**
   * Processes a hover event at the given screen coordinates
   */
  private processHover(clientX: number, clientY: number): void {
    // Convert screen coordinates to canvas coordinates
    const viewport = this.gfx.viewport;
    if (!viewport) {
      this.setHoveredElement(null);
      return;
    }

    // Get element at point using gfx
    const point = viewport.toModelCoord(clientX, clientY);
    const element = this.gfx.getElementByPoint(point[0], point[1]);

    this.setHoveredElement(element?.id ?? null);
  }

  /**
   * Updates the hovered element and triggers highlighting
   */
  private setHoveredElement(elementId: string | null): void {
    if (this.hoveredElementId === elementId) return;

    this.hoveredElementId = elementId;
    this.updateHighlighting();
  }

  // ===========================================================================
  // Selection Tracking
  // ===========================================================================

  /**
   * Sets up selection tracking via slots or polling
   */
  private setupSelectionTracking(): void {
    // Try to use selection slots if available
    const selection = this.gfx.selection;

    if (selection && 'slots' in selection) {
      // Use slots for reactive updates - try different API patterns
      const slots = (selection as unknown as { slots: Record<string, unknown> })
        .slots as {
        updated?: {
          on?: (cb: () => void) => { dispose: () => void };
          subscribe?: (cb: () => void) => { unsubscribe: () => void };
        };
      };

      if (slots?.updated) {
        // Try .on() first (BlockSuite pattern)
        if (typeof slots.updated.on === 'function') {
          const disposable = slots.updated.on(() => {
            this.syncSelectionState();
          });
          this.cleanupFns.push(() => disposable.dispose());
          return;
        }

        // Try .subscribe() (RxJS pattern)
        if (typeof slots.updated.subscribe === 'function') {
          const subscription = slots.updated.subscribe(() => {
            this.syncSelectionState();
          });
          this.cleanupFns.push(() => subscription.unsubscribe());
          return;
        }
      }
    }

    // Fallback: Poll selection state
    this.selectionPollInterval = setInterval(() => {
      this.syncSelectionState();
    }, 100); // 10 times per second
  }

  /**
   * Syncs selection state from gfx.selection
   */
  private syncSelectionState(): void {
    const selection = this.gfx.selection;
    if (!selection) return;

    const selectedElements: GfxModel[] = selection.selectedElements ?? [];
    const newSelectedIds = new Set(selectedElements.map(el => el.id));

    // Check if selection changed
    if (this.setsAreEqual(this.selectedElementIds, newSelectedIds)) {
      return;
    }

    this.selectedElementIds = newSelectedIds;
    this.updateHighlighting();
  }

  // ===========================================================================
  // Highlighting Logic
  // ===========================================================================

  /**
   * Updates edge highlighting based on current hover/selection state
   */
  private updateHighlighting(): void {
    // Collect all element IDs that should have their edges highlighted
    const activeElementIds = new Set<string>();
    // Only File-Index row hover/selection owns the additional source-node
    // outline. Regular canvas hover/selection already has native affordances.
    const manuallyHighlightedElementIds = new Set<string>();

    // Add hovered element
    if (this.hoveredElementId) {
      activeElementIds.add(this.hoveredElementId);
    }

    // Add selected elements
    for (const id of this.selectedElementIds) {
      activeElementIds.add(id);
    }

    // Add manually highlighted elements, e.g. File-Index row hover/selection.
    for (const elementIds of this.manualElementHighlightScopes.values()) {
      for (const id of this.resolveFileIndexElementIdsForSourceBlocks(
        elementIds
      )) {
        activeElementIds.add(id);
        manuallyHighlightedElementIds.add(id);
      }
    }

    // Collect all edges that should be highlighted
    const edgesToHighlight = new Set<string>();
    for (const elementId of activeElementIds) {
      const edges = this.renderingService.getEdgesForElement(elementId);
      for (const edge of edges) {
        edgesToHighlight.add(edge.id);
      }
    }

    // Remove highlights from edges that are no longer active
    for (const edgeId of this.highlightedEdgeIds) {
      if (!edgesToHighlight.has(edgeId)) {
        this.renderingService.setEdgeHighlight(edgeId, false);
      }
    }

    // Add highlights to newly active edges
    for (const edgeId of edgesToHighlight) {
      if (!this.highlightedEdgeIds.has(edgeId)) {
        this.renderingService.setEdgeHighlight(edgeId, true);
      }
    }

    // Update tracked state
    this.highlightedEdgeIds = edgesToHighlight;
    this.syncElementHighlightClasses(manuallyHighlightedElementIds);
    this.publishActiveElementsChanged(activeElementIds);
  }

  /**
   * Clears all edge highlights
   */
  private clearAllHighlights(): void {
    for (const edgeId of this.highlightedEdgeIds) {
      this.renderingService.setEdgeHighlight(edgeId, false);
    }
    this.highlightedEdgeIds.clear();
    this.syncElementHighlightClasses(new Set());
  }

  // ===========================================================================
  // Public API
  // ===========================================================================

  /**
   * Manually triggers highlighting for specific elements
   */
  highlightElementEdges(elementIds: string[]): void {
    for (const elementId of elementIds) {
      const edges = this.renderingService.getEdgesForElement(elementId);
      for (const edge of edges) {
        this.renderingService.setEdgeHighlight(edge.id, true);
        this.highlightedEdgeIds.add(edge.id);
      }
    }
  }

  setManualElementHighlights(
    scope: string,
    elementIds: readonly string[]
  ): void {
    const next = new Set(elementIds.filter(Boolean));
    const current = this.manualElementHighlightScopes.get(scope) ?? new Set();
    if (this.setsAreEqual(current, next)) {
      return;
    }

    if (next.size === 0) {
      this.manualElementHighlightScopes.delete(scope);
    } else {
      this.manualElementHighlightScopes.set(scope, next);
    }
    this.updateHighlighting();
  }

  clearManualElementHighlights(scope: string): void {
    if (!this.manualElementHighlightScopes.delete(scope)) {
      return;
    }
    this.updateHighlighting();
  }

  /**
   * Manually clears highlighting for specific elements
   */
  unhighlightElementEdges(elementIds: string[]): void {
    for (const elementId of elementIds) {
      const edges = this.renderingService.getEdgesForElement(elementId);
      for (const edge of edges) {
        // Only unhighlight if not hovered or selected
        const isStillActive =
          this.hoveredElementId === edge.sourceId ||
          this.hoveredElementId === edge.targetId ||
          this.selectedElementIds.has(edge.sourceId) ||
          this.selectedElementIds.has(edge.targetId);

        if (!isStillActive) {
          this.renderingService.setEdgeHighlight(edge.id, false);
          this.highlightedEdgeIds.delete(edge.id);
        }
      }
    }
  }

  /**
   * Updates configuration at runtime
   */
  setConfig(config: Partial<EdgeHighlightConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Gets current configuration
   */
  getConfig(): EdgeHighlightConfig {
    return { ...this.config };
  }

  // ===========================================================================
  // Utilities
  // ===========================================================================

  /**
   * Checks if two sets contain the same elements
   */
  private setsAreEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const item of a) {
      if (!b.has(item)) return false;
    }
    return true;
  }

  private syncElementHighlightClasses(nextElementIds: Set<string>): void {
    for (const elementId of this.highlightedElementIds) {
      if (nextElementIds.has(elementId)) continue;
      this.getElementView(elementId)?.classList.remove(
        FILE_INDEX_SOURCE_HIGHLIGHT_CLASS
      );
    }

    for (const elementId of nextElementIds) {
      if (this.highlightedElementIds.has(elementId)) continue;
      this.getElementView(elementId)?.classList.add(
        FILE_INDEX_SOURCE_HIGHLIGHT_CLASS
      );
    }

    this.highlightedElementIds = new Set(nextElementIds);
  }

  private publishActiveElementsChanged(elementIds: Set<string>): void {
    const sourceBlockIds =
      this.resolveFileIndexSourceBlockIdsForElementIds(elementIds);
    if (this.setsAreEqual(this.publishedSourceBlockIds, sourceBlockIds)) {
      return;
    }

    this.publishedSourceBlockIds = new Set(sourceBlockIds);
    this.container?.dispatchEvent(
      new CustomEvent<FileIndexSourceElementHighlightChangeDetail>(
        FILE_INDEX_HIGHLIGHT_EVENTS.sourceElementHighlightChange,
        {
          bubbles: true,
          composed: true,
          detail: {
            docId: this.getDocId(),
            sourceBlockIds: [...sourceBlockIds],
          },
        }
      )
    );
  }

  private getElementView(elementId: string): HTMLElement | null {
    const view = (
      this.gfx as unknown as { view?: { get?: (id: string) => unknown } }
    ).view?.get?.(elementId);
    if (view instanceof HTMLElement) {
      return view;
    }
    const element = (view as { element?: unknown; host?: unknown } | null)
      ?.element;
    if (element instanceof HTMLElement) {
      return element;
    }
    const host = (view as { element?: unknown; host?: unknown } | null)?.host;
    return host instanceof HTMLElement ? host : null;
  }

  private getDocId(): string | null {
    return (
      ((this.gfx as unknown as { doc?: { id?: string } }).doc?.id as
        | string
        | undefined) ?? null
    );
  }

  private resolveFileIndexElementIdsForSourceBlocks(
    sourceBlockIds: Iterable<string>
  ): string[] {
    const sourceBlockIdSet = new Set([...sourceBlockIds].filter(Boolean));
    const elementIds = new Set(sourceBlockIdSet);
    if (sourceBlockIdSet.size === 0) return [];

    for (const edge of this.renderingService.getAllEdges()) {
      const metadata = edge.metadata;
      if (!metadata || !isFileIndexEdgeMetadata(metadata)) continue;
      if (!sourceBlockIdSet.has(metadata.sourceBlockId)) continue;
      elementIds.add(edge.targetId);
    }

    return [...elementIds];
  }

  private resolveFileIndexSourceBlockIdsForElementIds(
    elementIds: Set<string>
  ): Set<string> {
    const sourceBlockIds = new Set<string>();
    if (elementIds.size === 0) return sourceBlockIds;

    for (const edge of this.renderingService.getAllEdges()) {
      const metadata = edge.metadata;
      if (!metadata || !isFileIndexEdgeMetadata(metadata)) continue;
      if (elementIds.has(edge.targetId)) {
        sourceBlockIds.add(metadata.sourceBlockId);
      }
    }

    return sourceBlockIds;
  }
}
